import { effect, inject, Injectable, Signal, untracked } from '@angular/core';
import { isDefined, Store } from '@softline/core';
import {
  JwtAuthenticationStore,
  SOFTLINE_FEATURE_JWT_AUTHENTICATION,
  UserInfo,
  UserInfoService,
} from '@softline/auth';
import { SOFTLINE_FEATURE_UTILITIES_VKFORGS } from '@softapps/core/context';
import { VkforgStore } from '@softapps/core/context';
import { Vkforg } from '@softapps/core/context';
import { ContextStorageService } from '@softapps/core/context';

export interface VkforgClaims extends UserInfo {
  vkforgid?: number;
  firmaid?: number;
}

/**
 * The issued token does not always carry a vkforg claim (the backend then applies its
 * own default). Every consumer resolves the active organisation as
 * `vkforgs.find(id) ?? vkforgs[0]`, so the UI would show the first organisation of the
 * list while the backend loads the data of another one.
 *
 * Commits the resolved organisation into the token once per session so both sides agree.
 *
 * TODO: remove once the backend always issues vkforgid/firmaid claims.
 */
@Injectable()
export class VkforgClaimSyncService {
  private readonly store = inject(Store);
  private readonly userInfo = inject<UserInfoService<VkforgClaims>>(UserInfoService);
  private readonly contextStorage = inject(ContextStorageService, { optional: true });

  private readonly vkforgs = this.store.signal(
    SOFTLINE_FEATURE_UTILITIES_VKFORGS,
    VkforgStore.getters.all
  ) as Signal<Vkforg[]>;

  // a single attempt: a backend that keeps omitting the claim must not loop us
  private attempted = false;

  constructor() {
    effect(() => {
      const claims = this.userInfo.value();
      const vkforgs = this.vkforgs();
      if (this.attempted || !claims || isDefined(claims.vkforgid)) return;
      if (!vkforgs?.length) return;
      if (!this.store.isRegistered(SOFTLINE_FEATURE_JWT_AUTHENTICATION)) return;
      // the persisted context may still hold the user's real preference even
      // though the fresh token omitted the claim — wait for it to resolve
      // rather than defaulting to an arbitrary first list entry
      if (this.contextStorage?.isLoading()) return;
      const storedVkforgId = (
        this.contextStorage?.value() as { vkforgid?: number } | null
      )?.vkforgid;
      const fallback = vkforgs.find((v) => v.id === storedVkforgId) ?? vkforgs[0];
      this.attempted = true;
      untracked(() => void this.commit(claims, fallback));
    });
  }

  private async commit(claims: VkforgClaims, vkforg: Vkforg): Promise<void> {
    await this.store.dispatch(
      SOFTLINE_FEATURE_JWT_AUTHENTICATION,
      JwtAuthenticationStore.actions.change,
      { ...claims, vkforgid: vkforg.id, firmaid: vkforg.idfirma }
    );
  }
}
